import { forwardRef, useState } from "react";
import styled from "styled-components";
import { EyeIcon, EyeSlashedIcon } from "ui/icons";
import { themeColor, themeFontSize } from "ui/theme";
import { HelperText } from "ui/base/helperText/HelperText";

export const InputSignUp = forwardRef(
    (
        {
            center = false,
            value = "",
            placeholder = "",
            label,
            size = "regular",
            type = "text",
            icon,
            iconPosition = "left",
            setValue,
            isError = false,
            errorText,
            id,
            maxLength,
            minLength,
            showLimit,
            labelSize,
            labelWeight,
            gap,
            inputCenterText,
            onBlur,
            centerInput,
            placeholderColor = "placeholder",
            placeholderPosition = "left",
            placeholderSize,
            showIconWhenEmpty = false,
            disabled = false,
            inputContainerStyle,
            Tooltip,
            ...rest
        },
        ref
    ) => {
        const [hide, setHide] = useState(false);
        const [showPassword, setShowPassword] = useState(type);

        const onChangeHandler = (el) => {
            if (!disabled) {
                setValue(el.target.value);
            }
        };

        const togglePassword = () => {
            if (!disabled) {
                setShowPassword((prev) => (prev === "text" ? "password" : "text"));
            }
        };

        return (
            <InputWrapper>
                {label && (
                    <LabelContainer>
                        <HelperText
                            htmlFor={id}
                            size={labelSize}
                            weight={labelWeight}
                            className="pre-line"
                        >
                            {label}
                        </HelperText>
                        {Tooltip && Tooltip}
                    </LabelContainer>
                )}

                <InputContainer
                    isError={isError}
                    style={inputContainerStyle}
                    htmlFor={id}
                    gap={gap}
                    focus={hide}
                    disabled={disabled}
                >
                    {((icon && iconPosition == "left") ||
                        (showIconWhenEmpty && value == "")) && (
                        <InputIcon
                            isError={isError}
                            htmlFor={id}
                            hasValue={!!value}
                            disabled={disabled}
                        >
                            {icon}
                        </InputIcon>
                    )}

                    <InputStyled
                        ref={ref}
                        value={value}
                        placeholder={placeholder}
                        onChange={onChangeHandler}
                        size={size}
                        placeholderSize={placeholderSize}
                        type={showPassword}
                        icon={!!icon}
                        id={id}
                        hasValue={!!value && centerInput}
                        maxLength={maxLength}
                        minLength={minLength}
                        onFocus={() => !disabled && setHide(true)}
                        onBlur={() => {
                            if (onBlur) onBlur();
                            setHide(false);
                        }}
                        iconPosition={value ? iconPosition : "left"}
                        center={inputCenterText}
                        placeholderColor={placeholderColor}
                        placeholderPosition={placeholderPosition}
                        disabled={disabled}
                        {...rest}
                    />

                    {icon && iconPosition == "right" && value != "" && (
                        <InputIcon
                            isError={isError}
                            htmlFor={id}
                            hasValue={!!value}
                            disabled={disabled}
                        >
                            {icon}
                        </InputIcon>
                    )}

                    {type === "password" && (
                        <InputEyeIcon
                            isError={isError}
                            onClick={togglePassword}
                            hasValue={!!value}
                            disabled={disabled}
                        >
                            {showPassword === "password" ? <EyeIcon /> : <EyeSlashedIcon />}
                        </InputEyeIcon>
                    )}
                </InputContainer>

                {showLimit && value && hide ? (
                    <ShowLimitCharacterContainer>
                        {showLimitCharacter(value, maxLength, minLength)}
                    </ShowLimitCharacterContainer>
                ) : null}

                {isError && errorText ? (
                    <HelperText color="pink" size="small">
                        {errorText}
                    </HelperText>
                ) : null}
            </InputWrapper>
        );
    }
);

const showLimitCharacter = (value, maxLength, minLength) => {
    if (!value) return null;

    if (maxLength > 0 && minLength === 0) {
        return (
            <HelperText size="regular" sizeMobile="small" color="placeholder">
                {value.length} / {maxLength}
            </HelperText>
        );
    }

    if (value.length < minLength) {
        return (
            <HelperText size="regular" sizeMobile="small" color="placeholder">
                {minLength - value.length} more required
            </HelperText>
        );
    }

    return (
        <HelperText size="regular" sizeMobile="small" color="placeholder">
            {value.length} / {maxLength}
        </HelperText>
    );
};

const InputWrapper = styled.div`
    width: 100%;
    display: grid;
    gap: 0.7rem;
    font-family: var(--font-primary);

    & .pre-line {
        white-space: pre-line;
    }
`;

const LabelContainer = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
`;

const InputContainer = styled.label`
    display: flex;
    align-items: center;
    gap: ${({ gap }) => gap || "1rem"};
    height: 54px;
    border-width: 1px;
    border-style: solid;
    border-color: ${({ isError, focus, disabled }) =>
            disabled
                    ? "rgb(167, 166, 206,0.1)"
                    : isError
                            ? themeColor("pink")
                            : focus
                                    ? themeColor("blue")
                                    : themeColor("mobileInputBorder")};
    border-radius: 5px;
    padding-inline: ${({ gap }) => gap || "1rem"};
    font-family: var(--font-primary);
    cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
    background-color: ${({ disabled }) =>
            disabled ? "rgb(167, 166, 206,0.10)" : "transparent"};
`;

const InputIcon = styled.label`
    height: 100%;
    width: 30px;
    min-width: 30px;
    max-width: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: ${({ isError, hasValue, disabled }) =>
            disabled
                    ? "rgb(167, 166, 206,1)"
                    : isError
                            ? themeColor("pink")
                            : hasValue
                                    ? themeColor("blue")
                                    : themeColor("mobileInputBorder")};

    & > svg {
        width: 20px;
        height: 20px;
        object-fit: contain;
    }
`;

const InputEyeIcon = styled.button`
    padding: 0;
    outline: none;
    border: none;
    margin: 0;
    background-color: transparent;
    height: 100%;
    width: 30px;
    min-width: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: ${({ isError, hasValue, disabled }) =>
            disabled
                    ? "rgb(167, 166, 206,1)"
                    : isError
                            ? themeColor("pink")
                            : hasValue
                                    ? themeColor("blue")
                                    : themeColor("mobileInputBorder")};
    cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};

    & > svg {
        width: 20px;
        height: 20px;
        object-fit: contain;
    }
`;

const InputStyled = styled.input`
    width: ${({ hasValue }) => (hasValue ? "50%" : "100%")};
    height: 100%;
    font-size: ${({ size }) => themeFontSize(size || "regular")};
    font-family: var(--font-primary);
    border: none;
    outline: none;
    border-radius: 8px;
    text-align: ${({ center, iconPosition }) =>
            center ? "center" : iconPosition};
    color: ${({ disabled }) =>
            disabled ? "rgb(167, 166, 206,1)" : themeColor("txt")};

    &::placeholder {
        color: ${({ placeholderColor }) => themeColor(placeholderColor)};
        font-weight: 400;
        text-align: ${({ placeholderPosition }) =>
                placeholderPosition ?? "left"};
        font-size: ${({ placeholderSize }) =>
                placeholderSize ? themeFontSize(placeholderSize) : "inherit"};
    }

    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    cursor: ${({ disabled }) => (disabled ? "not-allowed" : "text")};
`;

const ShowLimitCharacterContainer = styled.div`
    display: flex;
    justify-content: flex-end;
`;
